import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signUp, confirmSignUp, signOut as amplifySignOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { AWS_CONFIG } from '../config/aws';
import { toE164, buildFakeEmail, isPhoneInput } from '../utils/phone';

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: AWS_CONFIG.cognito.userPoolId,
      userPoolClientId: AWS_CONFIG.cognito.userPoolClientId,
    },
  },
});

const SOCIAL_TOKEN_KEY = 'newcar_social_tokens';
const PROFILE_OVERRIDES_KEY = 'newcar_profile_overrides';
const AuthContext = createContext(null);

function decodeJwtPayload(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return JSON.parse(new TextDecoder('utf-8').decode(bytes));
  } catch { return {}; }
}

function getProfileOverrides(key) {
  if (!key) return {};
  try {
    const all = JSON.parse(localStorage.getItem(PROFILE_OVERRIDES_KEY) || '{}');
    return all[key] || {};
  } catch { return {}; }
}

function saveProfileOverrides(key, overrides) {
  if (!key || !overrides) return;
  try {
    const all = JSON.parse(localStorage.getItem(PROFILE_OVERRIDES_KEY) || '{}');
    all[key] = { ...(all[key] || {}), ...overrides };
    localStorage.setItem(PROFILE_OVERRIDES_KEY, JSON.stringify(all));
  } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingPhoneVerification, setPendingPhoneVerification] = useState(null);

  const checkUser = useCallback(async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const payload = session.tokens?.idToken?.payload || {};
      const groups = payload['cognito:groups'] || [];
      const overrides = getProfileOverrides(payload.email || payload.sub);
      setUser({
        ...currentUser,
        name: overrides.name || payload.name,
        email: overrides.email || payload.email,
        phoneNumber: overrides.phoneNumber || payload.phone_number,
      });
      setIsAdmin(groups.includes('admin'));
      setIsLoading(false);
      return;
    } catch {}
    try {
      const stored = localStorage.getItem(SOCIAL_TOKEN_KEY);
      if (stored) {
        const { idToken } = JSON.parse(stored);
        const payload = decodeJwtPayload(idToken);
        if (payload.exp * 1000 > Date.now()) {
          const groups = payload['cognito:groups'] || [];
          const overrides = getProfileOverrides(payload.email || payload.sub);
          setUser({
            username: payload.email || payload.sub,
            signInDetails: { loginId: payload.email },
            name: overrides.name || payload.name,
            email: overrides.email || payload.email,
            phoneNumber: overrides.phoneNumber || payload.phone_number,
          });
          setIsAdmin(groups.includes('admin'));
          setIsLoading(false);
          return;
        }
        localStorage.removeItem(SOCIAL_TOKEN_KEY);
      }
    } catch {}
    setUser(null); setIsAdmin(false); setIsLoading(false);
  }, []);

  useEffect(() => { checkUser(); }, [checkUser]);

  const refreshUser = useCallback(async (overrides) => {
    if (overrides) {
      const key = user?.email || user?.username;
      if (key) saveProfileOverrides(key, overrides);
    }
    await checkUser();
    if (overrides) {
      setUser(prev => prev ? { ...prev, ...overrides } : prev);
    }
  }, [checkUser, user]);

  async function handleSignIn(emailOrPhone, password) {
    try { await amplifySignOut(); } catch {}
    const username = isPhoneInput(emailOrPhone) ? buildFakeEmail(emailOrPhone) : String(emailOrPhone || '').trim();
    const result = await signIn({ username, password });
    if (result.isSignedIn) {
      try { localStorage.setItem('lastProvider', 'email'); } catch {}
      await checkUser();
    }
    return result;
  }

  async function handleSignUp(email, password, name, phone) {
    const trimmedEmail = (email || '').trim();
    const phoneE164 = toE164(phone) || phone;
    const username = trimmedEmail || buildFakeEmail(phone);
    const result = await signUp({
      username, password,
      options: { userAttributes: { email: username, name, phone_number: phoneE164 } },
    });
    // PreSignUp Lambda autoConfirm 시 isSignUpComplete=true → 즉시 로그인
    if (result.isSignUpComplete) {
      await handleSignIn(username, password);
      // Sync UserProfiles DDB so admin/profiles page sees this signup. Same path
      // ProfileSetupModal uses for social signups — keeps the two flows consistent.
      // Non-blocking: signup succeeds even if this sync fails (network, etc.).
      try {
        const token = await getIdToken();
        if (token) {
          await fetch(`${AWS_CONFIG.apiUrl}/profile/update`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: token },
            body: JSON.stringify({ phoneNumber: phone, name, email: trimmedEmail }),
          });
        }
      } catch (err) {
        console.warn('[handleSignUp] UserProfiles sync failed (non-blocking):', err?.message || err);
      }
    }
    return { ...result, usernameForLogin: username };
  }

  async function handleConfirmSignUp(email, code) {
    return await confirmSignUp({ username: email, confirmationCode: code });
  }

  async function handleSignOut() {
    try { await amplifySignOut(); } catch {}
    localStorage.removeItem(SOCIAL_TOKEN_KEY);
    setUser(null); setIsAdmin(false);
  }

  async function socialSignIn(provider, data) {
    try { await amplifySignOut(); } catch {}
    localStorage.removeItem(SOCIAL_TOKEN_KEY);
    const res = await fetch(`${AWS_CONFIG.apiUrl}/auth/${provider}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    const tokens = await res.json();
    if (!res.ok) throw new Error(tokens.error || 'Social login failed');
    localStorage.setItem(SOCIAL_TOKEN_KEY, JSON.stringify(tokens));
    await checkUser();
    
    const enforced = process.env.REACT_APP_IDENTITY_ENFORCEMENT === 'true';
    if (enforced && tokens.profileLinked === false) {
      const payload = decodeJwtPayload(tokens.idToken);
      setPendingPhoneVerification({ sub: tokens.sub, provider, name: payload.name, email: payload.email });
    }
    return tokens;
  }

  async function getIdToken() {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (token) return token;
    } catch {}
    try {
      const stored = localStorage.getItem(SOCIAL_TOKEN_KEY);
      if (!stored) return null;
      const { idToken } = JSON.parse(stored);
      const payload = decodeJwtPayload(idToken);
      if (payload.exp * 1000 < Date.now() + 30000) {
        localStorage.removeItem(SOCIAL_TOKEN_KEY);
        setUser(null); setIsAdmin(false); return null;
      }
      return idToken;
    } catch {}
    return null;
  }

  const needsProfileSetup = !isLoading && !!user && !user.phoneNumber;

  return (
    <AuthContext.Provider value={{
      user, isAdmin, isLoading,
      signIn: handleSignIn,
      signUp: handleSignUp,
      confirmSignUp: handleConfirmSignUp,
      signOut: handleSignOut,
      socialSignIn,
      getIdToken,
      refreshUser,
      needsProfileSetup,
      pendingPhoneVerification,
      clearPendingPhoneVerification: () => setPendingPhoneVerification(null),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
